import React, { useMemo } from "react";
import "./index.css";
import { DatePicker } from "rsuite";
import moment from "moment";

import {
  Auth0AccountRole,
  BuiltinField,
  Condition,
  ComparisonOperator,
  OrganizationDataKeyResponse,
  DataType,
} from "client/openapi";
import { Button, ButtonColor, ButtonSize } from "components/Button";
import { TrashIcon } from "@radix-ui/react-icons";
import { Select } from "components/Select";
import {
  BUILTIN_FIELDS,
  OPERATORS_BY_TYPE,
  RELATIVE_DATE_RANGES,
  builtinFieldInfo,
  builtinOperators,
  takesList,
} from "./validation";

export type SubjectOption = { id: number; name: string };
export type FormOption = { id: number; name: string };

type ConditionEditorProps = {
  condition: Condition;
  onChange: (updated: Condition) => void;
  onRemove: () => void;
  availableKeys: OrganizationDataKeyResponse[];
  /** Whose fields these are: the built-in fields they have can be picked. */
  personType: Auth0AccountRole;
  /** The organization's subjects, for the built-in Subjects field. */
  subjects?: SubjectOption[];
  /** The organization's forms, for the built-in form fields. */
  forms?: FormOption[];
};

// Payment method comes from Stripe; these are its only values.
const paymentMethodOptions = [
  { value: "card", label: "Card" },
  { value: "bank", label: "Bank" },
  { value: "card_and_bank", label: "Card and bank" },
  { value: "none", label: "None" },
];

const comparisonOptions = [
  { value: ComparisonOperator.EQUALS, label: "equals" },
  { value: ComparisonOperator.NOT_EQUALS, label: "not_equals" },
  { value: ComparisonOperator.GREATER_THAN, label: "greater_than" },
  {
    value: ComparisonOperator.GREATER_THAN_OR_EQUAL,
    label: "greater_than_or_equal",
  },
  { value: ComparisonOperator.LESS_THAN, label: "less_than" },
  { value: ComparisonOperator.LESS_THAN_OR_EQUAL, label: "less_than_or_equal" },
  { value: ComparisonOperator.ILIKE, label: "ilike (contains)" },
  { value: ComparisonOperator.IN, label: "in" },
  { value: ComparisonOperator.NOT_IN, label: "not_in" },
  { value: ComparisonOperator.WITHIN, label: "is within" },
];

/**
 * The relative ranges, listed beside a date field's calendar. rsuite gives
 * each shortcut a date, so each carries its own stand-in Date, and the
 * picker's onChange knows a range by that very object (its time is unused).
 */
const RANGE_SHORTCUTS = RELATIVE_DATE_RANGES.map((range) => ({
  label: range.label,
  value: new Date(),
  placement: "left" as const,
}));

const YES_NO = [
  { value: "true", label: "Yes" },
  { value: "false", label: "No" },
];

export function ConditionEditor({
  condition,
  onChange,
  onRemove,
  availableKeys,
  personType,
  subjects,
  forms,
}: ConditionEditorProps) {
  const { org_data_key_id, comparison_operator, value, negate } = condition;
  const builtin = builtinFieldInfo(condition.builtin_field);
  const builtinOptions = BUILTIN_FIELDS.filter((info) =>
    info.people.includes(personType)
  ).map((info) => ({ value: info.field as string, label: info.label }));

  const selectedKey = useMemo(
    () => availableKeys.find((k) => k.id === org_data_key_id),
    [org_data_key_id, availableKeys]
  );

  const dataType = selectedKey?.data_type || DataType.TEXT;

  /** When user changes the field, reset operator/value by default */
  function handleFieldChange(fieldValue: string) {
    const {
      org_data_key_id: _key,
      builtin_field: _builtin,
      form_id: formId,
      ...rest
    } = condition;
    const picked = BUILTIN_FIELDS.find((info) => info.field === fieldValue);
    if (picked) {
      onChange({
        ...rest,
        builtin_field: picked.field,
        // Between the two form fields, the form stays picked.
        ...(picked.form && builtin?.form && formId ? { form_id: formId } : {}),
        comparison_operator: ComparisonOperator.EQUALS,
        // Form filled out starts at "is yes", so picking the form is enough.
        value: picked.field === BuiltinField.FORM_FILLED_OUT ? true : "",
      });
      return;
    }
    onChange({
      ...rest,
      org_data_key_id: Number(fieldValue),
      comparison_operator: ComparisonOperator.EQUALS,
      value: "",
    });
  }

  function handleOperatorChange(newOp: ComparisonOperator) {
    // A list operator takes a list, "within" a range and any other one
    // value, so the value starts again when the operator changes between them.
    const within = ComparisonOperator.WITHIN;
    const keepsValue =
      takesList(newOp) === takesList(comparison_operator) &&
      (newOp === within) === (comparison_operator === within);
    onChange({
      ...condition,
      comparison_operator: newOp,
      value: keepsValue ? value : takesList(newOp) ? [] : "",
    });
  }

  function handleValueChange(newVal: any) {
    onChange({ ...condition, value: newVal });
  }

  /**
   * A date field's picker: a range makes the rule "is within" it; a date
   * keeps a date operator ("equals" again after a range).
   */
  function handleDatePicked(date: Date | null) {
    const shortcut = RANGE_SHORTCUTS.findIndex((s) => s.value === date);
    if (shortcut >= 0) {
      onChange({
        ...condition,
        comparison_operator: ComparisonOperator.WITHIN,
        value: RELATIVE_DATE_RANGES[shortcut].value,
      });
      return;
    }
    onChange({
      ...condition,
      comparison_operator:
        comparison_operator === ComparisonOperator.WITHIN
          ? ComparisonOperator.EQUALS
          : comparison_operator,
      value: date ? moment(date).startOf("day").format("YYYY-MM-DD") : "",
    });
  }

  function toggleNegate() {
    onChange({ ...condition, negate: !negate });
  }

  // Helper to handle list input values
  const isListOperator = takesList(comparison_operator);

  const handleListInputChange = (
    inputValue: string,
    type: "text" | "number"
  ) => {
    if (!isListOperator) {
      // For non-list operators, handle as before
      if (type === "number") {
        handleValueChange(inputValue ? parseFloat(inputValue) : null);
      } else {
        handleValueChange(inputValue);
      }
    } else {
      // For list operators, just store the raw input
      handleValueChange(inputValue);
    }
  };

  let valueInput: React.ReactNode = null;

  const subjectOptions = (subjects || []).map((subject) => ({
    value: String(subject.id),
    label: subject.name,
  }));
  const subjectLabel = (id: number) =>
    subjects?.find((subject) => subject.id === id)?.name || "";

  // A built-in field takes what a field of its type takes; Form filled out
  // takes yes or no.
  switch (
    builtin?.field === BuiltinField.FORM_FILLED_OUT
      ? "FORM_FILLED_OUT"
      : builtin
      ? builtin.type
      : dataType
  ) {
    case "PAYMENT_METHOD":
      valueInput = (
        <Select
          className="min-w-[220px]"
          value={
            isListOperator
              ? paymentMethodOptions.filter(
                  (o) =>
                    Array.isArray(value) && value.includes(o.value as never)
                )
              : paymentMethodOptions.find((o) => o.value === value) || null
          }
          onChange={(option) => {
            if (isListOperator) {
              handleValueChange(option ? option.map((opt) => opt.value) : []);
            } else {
              handleValueChange(option ? option.value : "");
            }
          }}
          options={paymentMethodOptions}
          placeholder="--Select--"
          isClearable
          isMulti={isListOperator}
        />
      );
      break;

    case "SUBJECTS":
      valueInput = (
        <Select
          className="min-w-[220px]"
          value={
            isListOperator
              ? Array.isArray(value)
                ? value.map((v) => ({
                    value: String(v),
                    label: subjectLabel(Number(v)),
                  }))
                : []
              : typeof value === "number"
              ? { value: String(value), label: subjectLabel(value) }
              : null
          }
          onChange={(option) => {
            if (isListOperator) {
              handleValueChange(
                option ? option.map((opt) => Number(opt.value)) : []
              );
            } else {
              handleValueChange(option ? Number(option.value) : "");
            }
          }}
          options={subjectOptions}
          placeholder="--Select--"
          isClearable
          isMulti={isListOperator}
        />
      );
      break;

    case DataType.TEXT:
      valueInput = (
        <input
          className="input"
          type="text"
          value={String(value ?? "")}
          onChange={(e) => {
            handleListInputChange(e.target.value, "text");
          }}
          placeholder={
            isListOperator ? "Enter values separated by commas" : "Enter text"
          }
          style={{ width: 220 }}
        />
      );
      break;

    case DataType.FLOAT:
      valueInput = (
        <input
          className="input"
          type={isListOperator ? "text" : "number"}
          step="any"
          value={String(value ?? "")}
          onChange={(e) => {
            handleListInputChange(e.target.value, "number");
          }}
          placeholder={
            isListOperator
              ? "Enter numbers separated by commas"
              : builtin?.field === BuiltinField.BALANCE_OWED ||
                builtin?.field === BuiltinField.CREDIT_BALANCE
              ? "Enter dollars"
              : "Enter number"
          }
          style={{ width: 220 }}
        />
      );
      break;

    case DataType.DATE: {
      const range = RELATIVE_DATE_RANGES.find((r) => r.value === value);
      valueInput = (
        <DatePicker
          oneTap
          ranges={RANGE_SHORTCUTS}
          // A range shows by its name, in the place of a date.
          className={range ? "condition-date--range" : undefined}
          placeholder={
            range?.label ??
            (comparison_operator === ComparisonOperator.WITHIN
              ? "Pick a range"
              : "Pick a date or range")
          }
          value={
            typeof value === "string" && value && !range
              ? moment(value).toDate()
              : null
          }
          onChange={handleDatePicked}
          format="yyyy-MM-dd"
          style={{ width: 220 }}
          size="md"
          block
        />
      );
      break;
    }

    case DataType.BOOLEAN: {
      // Default to true if condition.value is not a boolean
      const booleanVal = typeof value === "boolean" ? value : true;

      valueInput = (
        <Select
          className="min-w-[220px]"
          value={
            isListOperator
              ? Array.isArray(value)
                ? value.map((v) => ({
                    value: String(v),
                    label: v ? "Yes" : "No",
                  }))
                : []
              : undefined
          }
          onChange={(option) => {
            if (isListOperator) {
              const values = option
                ? option.map((opt) => opt.value === "true")
                : [];
              handleValueChange(values);
            } else {
              handleValueChange(option?.value === "true");
            }
          }}
          options={[
            { value: "true", label: "Yes" },
            { value: "false", label: "No" },
          ]}
          isClearable={false}
          isMulti={isListOperator}
        />
      );
      break;
    }

    case "FORM_FILLED_OUT":
      valueInput = (
        <Select
          className="min-w-[220px]"
          value={YES_NO.find((o) => o.value === String(value)) || null}
          onChange={(option) =>
            handleValueChange(option ? option.value === "true" : "")
          }
          options={YES_NO}
          placeholder="--Select--"
          isClearable={false}
        />
      );
      break;

    case DataType.CUSTOM_DATA_TYPE: {
      const customValues = selectedKey?.custom_data_type_key?.values || [];

      valueInput = (
        <Select
          className="min-w-[220px]"
          value={
            isListOperator
              ? Array.isArray(value)
                ? value.map((v) => ({
                    value: String(v),
                    label: String(v),
                  }))
                : []
              : value != null
              ? {
                  value: String(value),
                  label: String(value),
                }
              : null
          }
          onChange={(option) => {
            if (isListOperator) {
              // Store the option_names directly for backend
              const values = option ? option.map((opt) => opt.value) : [];
              handleValueChange(values);
            } else {
              const val = option ? option.value : null;
              handleValueChange(val);
            }
          }}
          options={customValues.map((cv) => ({
            value: cv.option_name,
            label: cv.option_name,
          }))}
          placeholder="--Select--"
          isClearable
          isMulti={isListOperator}
        />
      );
      break;
    }

    default:
      valueInput = (
        <input
          className="input"
          type="text"
          value={String(value ?? "")}
          onChange={(e) => handleValueChange(e.target.value)}
          style={{ width: 220 }}
        />
      );
  }

  return (
    <div className="flex flex-row">
      <div className="condition-row">
        {/* Center column: existing content */}

        {/* Field selector: the built-in fields offered, then CRM fields */}
        <Select
          className="min-w-[275px]"
          value={
            builtin
              ? { value: builtin.field as string, label: builtin.label }
              : org_data_key_id
              ? {
                  value: org_data_key_id.toString(),
                  label:
                    availableKeys.find((k) => k.id === org_data_key_id)?.name ||
                    "",
                }
              : null
          }
          onChange={(option) => handleFieldChange(option ? option.value : "0")}
          options={[
            { value: "0", label: "-- select field --", isDisabled: true },
            ...builtinOptions,
            ...availableKeys.map((k) => ({
              value: k.id.toString(),
              label: k.name,
            })),
          ]}
          placeholder="-- select field --"
        />

        {/* A form field's form, picked before the rest */}
        {builtin?.form && (
          <Select
            className="min-w-[220px]"
            value={
              condition.form_id
                ? {
                    value: String(condition.form_id),
                    label:
                      forms?.find((form) => form.id === condition.form_id)
                        ?.name || "Deleted form",
                  }
                : null
            }
            onChange={(option) =>
              onChange({
                ...condition,
                form_id: option ? Number(option.value) : undefined,
              })
            }
            options={(forms || []).map((form) => ({
              value: String(form.id),
              label: form.name,
            }))}
            placeholder="-- select form --"
          />
        )}

        {/* Operator selector */}
        <Select
          className="min-w-[225px]"
          value={{
            value: comparison_operator,
            label:
              comparisonOptions.find((op) => op.value === comparison_operator)
                ?.label || "",
          }}
          onChange={(option) =>
            handleOperatorChange(option?.value as ComparisonOperator)
          }
          options={comparisonOptions.filter((op) =>
            (builtin
              ? builtinOperators(builtin)
              : OPERATORS_BY_TYPE[dataType]
            ).includes(op.value)
          )}
          placeholder="-- select operator --"
        />

        {valueInput}
      </div>
      <div className="mt-4 ml-4">
        <Button
          color={ButtonColor.PURPLE}
          size={ButtonSize.SMALL}
          onClick={onRemove}
        >
          <TrashIcon className="w-4 h-4 " />
        </Button>
      </div>
    </div>
  );
}
